import * as React from 'react';
import { cva } from 'class-variance-authority';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../../../lib/utils';

/**
 * Sidebar component with exact Hyperkit styling
 *
 * Hyperkit Specs:
 * - Width: 260px expanded, 72px collapsed
 * - Background: bg-secondary (white/grey-900)
 * - Border: 1px grey-200/grey-800
 * - Transition: 200ms ease
 */

const SidebarContext = React.createContext({
  collapsed: false,
  setCollapsed: () => {},
});

export function useSidebar() {
  const context = React.useContext(SidebarContext);
  if (!context) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return context;
}

export function SidebarProvider({ children, defaultCollapsed = false }) {
  const [collapsed, setCollapsed] = React.useState(defaultCollapsed);

  return (
    <SidebarContext.Provider value={{ collapsed, setCollapsed }}>
      {children}
    </SidebarContext.Provider>
  );
}

const sidebarVariants = cva(
  'fixed left-0 top-0 bottom-0 z-40 flex flex-col bg-white border-r border-grey-200 transition-all duration-200 ease-out dark:bg-grey-900 dark:border-grey-800',
  {
    variants: {
      collapsed: {
        true: 'w-[72px]',
        false: 'w-[260px]',
      },
    },
    defaultVariants: {
      collapsed: false,
    },
  }
);

export const Sidebar = React.forwardRef(
  ({ className, children, ...props }, ref) => {
    const { collapsed } = useSidebar();

    return (
      <aside
        ref={ref}
        className={cn(sidebarVariants({ collapsed }), className)}
        {...props}
      >
        {children}
      </aside>
    );
  }
);
Sidebar.displayName = 'Sidebar';

export const SidebarHeader = React.forwardRef(
  ({ className, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          'flex items-center gap-3 px-4 py-3.5 border-b border-grey-200 dark:border-grey-800',
          className
        )}
        {...props}
      >
        {children}
      </div>
    );
  }
);
SidebarHeader.displayName = 'SidebarHeader';

export const SidebarBrand = React.forwardRef(
  ({ className, logo, name, subtitle, ...props }, ref) => {
    const { collapsed } = useSidebar();

    return (
      <div ref={ref} className={cn('flex items-center gap-3', className)} {...props}>
        {/* Logo */}
        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center text-white font-bold text-sm shrink-0">
          {logo || 'TG'}
        </div>
        {/* Brand info - hidden when collapsed */}
        {!collapsed && (
          <div className="flex flex-col min-w-0 transition-opacity duration-200">
            <span className="text-sm font-semibold text-grey-900 dark:text-grey-50 truncate">
              {name}
            </span>
            {subtitle && (
              <span className="text-xs text-grey-500 dark:text-grey-400 truncate">
                {subtitle}
              </span>
            )}
          </div>
        )}
      </div>
    );
  }
);
SidebarBrand.displayName = 'SidebarBrand';

export const SidebarContent = React.forwardRef(
  ({ className, children, ...props }, ref) => {
    return (
      <nav
        ref={ref}
        className={cn('flex-1 overflow-y-auto p-2', className)}
        {...props}
      >
        {children}
      </nav>
    );
  }
);
SidebarContent.displayName = 'SidebarContent';

export const SidebarFooter = React.forwardRef(
  ({ className, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          'p-2 border-t border-grey-200 dark:border-grey-800',
          className
        )}
        {...props}
      >
        {children}
      </div>
    );
  }
);
SidebarFooter.displayName = 'SidebarFooter';

export const SidebarToggle = React.forwardRef(
  ({ className, ...props }, ref) => {
    const { collapsed, setCollapsed } = useSidebar();

    return (
      <button
        ref={ref}
        onClick={() => setCollapsed(!collapsed)}
        className={cn(
          'absolute -right-3 top-[72px] z-50 w-6 h-6 rounded-full',
          'bg-white border border-grey-200 shadow-elevation-sm',
          'flex items-center justify-center',
          'text-grey-500 hover:text-grey-900 hover:border-teal-500',
          'transition-all duration-150',
          'dark:bg-grey-800 dark:border-grey-700 dark:text-grey-400 dark:hover:text-grey-50',
          className
        )}
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        {...props}
      >
        {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
      </button>
    );
  }
);
SidebarToggle.displayName = 'SidebarToggle';

export const SidebarDivider = React.forwardRef(
  ({ className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn('h-px bg-grey-200 dark:bg-grey-800 my-3', className)}
        {...props}
      />
    );
  }
);
SidebarDivider.displayName = 'SidebarDivider';
